import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Typography,
  IconButton,
  Button,
  Icon,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Card
} from 'investira.react.components';
import { BaseTemplate } from '../../../components/templates';
import withMessage from '../../../hoc/withMessage';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import { AVALIACAO_MODELO, AVALIACAO_SESSOES_QUESTOES } from '../../../enums';
import services from '../../../services';
import Range from '../_components/Range';
import QuestaoRating from '../_components/QuestaoRating';

const MAX_DISCRIMINACOES = 13;
const TIPO_RESPOSTA = {
  MULTIPLA_ESCOLHA: 'multipla_escolha',
  NUMERICA: 'numerica',
  DICOTOMICA: 'dicotomica'
};
const SEM_LIMITE = 'none';
const MIN_OPERADORES = [
  { value: SEM_LIMITE, label: 'Sem limite' },
  { value: 'gte', label: '>=' },
  { value: 'gt', label: '>' }
];
const MAX_OPERADORES = [
  { value: SEM_LIMITE, label: 'Sem limite' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' }
];

const getOpcaoPorIndice = pIndice => `${pIndice}ª` || '';

const OPCOES_DISCRIMINACAO = Array(MAX_DISCRIMINACOES)
  .fill()
  .map((_, xIndex) => getOpcaoPorIndice(xIndex + 1));
const createMultiplaEscolhaItem = pIndex => ({
  id: Date.now() + pIndex,
  opcao: getOpcaoPorIndice(pIndex + 1),
  pergunta: '',
  discriminacao: '',
  points: null,
  rating: 1
});
const createFaixaNumerica = pIndex => ({
  id: Date.now() + pIndex,
  faixa: `${pIndex + 1}ª`,
  minOp: pIndex === 0 ? SEM_LIMITE : 'gte',
  min: '',
  maxOp: 'lt',
  max: '',
  discriminacao: '',
  points: null,
  rating: 1
});
const createOpcoesDicotomicas = () => [
  { id: 'sim', opcao: 'Sim', pergunta: 'Sim', discriminacao: '', points: null, rating: 1 },
  { id: 'nao', opcao: 'Não', pergunta: 'Não', discriminacao: '', points: null, rating: 1 }
];

const AvaliacoesSettingsQuestoesEdit = memo(props => {
  const navigate = useNavigate();

  const xSessoesQuestoes = Object.values(AVALIACAO_SESSOES_QUESTOES);
  const xModelos = Object.values(AVALIACAO_MODELO);

  // TODO: Mudar estes states para formik
  const [questao, setQuestao] = useState('');
  const [sessaoId, setSessaoId] = useState(xSessoesQuestoes?.[0]?.sessao_id || '');
  const [peso, setPeso] = useState(null);
  const [modelosSelecionados, setModelosSelecionados] = useState([]);
  const [tipoResposta, setTipoResposta] = useState(TIPO_RESPOSTA.MULTIPLA_ESCOLHA);
  const [opcoesMultiplaEscolha, setOpcoesMultiplaEscolha] = useState([
    createMultiplaEscolhaItem(0)
  ]);
  const [opcoesDicotomicas, setOpcoesDicotomicas] = useState(createOpcoesDicotomicas());
  const [faixasNumericas, setFaixasNumericas] = useState([createFaixaNumerica(0)]);
  const [questaoParaGestor, setQuestaoParaGestor] = useState(false);

  const { actions } = useAvaliacoesContext();
  const isQuestaoInterna = !questaoParaGestor;

  const handleUpdateMultiplaEscolha = (id, field, value) => {
    setOpcoesMultiplaEscolha(pCurrent =>
      pCurrent.map(xOpcao => {
        if (xOpcao.id !== id) {
          return xOpcao;
        }
        return { ...xOpcao, [field]: value };
      })
    );
  };

  const handleAddMultiplaEscolha = () => {
    setOpcoesMultiplaEscolha(pCurrent => {
      if (pCurrent.length >= MAX_DISCRIMINACOES) {
        return pCurrent;
      }

      return [...pCurrent, createMultiplaEscolhaItem(pCurrent.length)];
    });
  };

  const handleRemoveMultiplaEscolha = id => {
    setOpcoesMultiplaEscolha(pCurrent => {
      if (pCurrent.length <= 1) {
        return pCurrent;
      }

      return pCurrent
        .filter(xOpcao => xOpcao.id !== id)
        .map((xOpcao, xIndex) => ({
          ...xOpcao,
          opcao: getOpcaoPorIndice(xIndex + 1)
        }));
    });
  };
  const handleUpdateFaixaNumerica = (id, field, value) => {
    setFaixasNumericas(pCurrent =>
      pCurrent.map(xFaixa => {
        if (xFaixa.id !== id) {
          return xFaixa;
        }
        return { ...xFaixa, [field]: value };
      })
    );
  };
  const handleAddFaixaNumerica = () => {
    setFaixasNumericas(pCurrent => {
      if (pCurrent.length >= MAX_DISCRIMINACOES) {
        return pCurrent;
      }

      return [...pCurrent, createFaixaNumerica(pCurrent.length)];
    });
  };
  const handleRemoveFaixaNumerica = id => {
    setFaixasNumericas(pCurrent => {
      if (pCurrent.length <= 1) {
        return pCurrent;
      }

      return pCurrent
        .filter(xFaixa => xFaixa.id !== id)
        .map((xFaixa, xIndex) => ({
          ...xFaixa,
          faixa: `${xIndex + 1}ª`
        }));
    });
  };
  const handleUpdateDicotomica = (id, field, value) => {
    setOpcoesDicotomicas(pCurrent =>
      pCurrent.map(xOpcao => (xOpcao.id === id ? { ...xOpcao, [field]: value } : xOpcao))
    );
  };

  const handleToggleModelo = pModeloId => {
    setModelosSelecionados(pCurrent =>
      pCurrent.includes(pModeloId)
        ? pCurrent.filter(xModeloId => xModeloId !== pModeloId)
        : [...pCurrent, pModeloId]
    );
  };

  const handleSave = async () => {
    if (!questao.trim()) {
      props.onMessageError?.('Informe o título da questão.');
      return;
    }

    if (!sessaoId) {
      props.onMessageError?.('Selecione a sessão da questão.');
      return;
    }

    if (modelosSelecionados.length === 0) {
      props.onMessageError?.('Selecione ao menos um modelo.');
      return;
    }

    if (
      !isQuestaoInterna &&
      tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA &&
      opcoesMultiplaEscolha.some(xOpcao => !xOpcao.pergunta?.trim())
    ) {
      props.onMessageError?.('Preencha a pergunta de todas as opções de múltipla escolha.');
      return;
    }

    if (tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA && opcoesMultiplaEscolha.length === 0) {
      props.onMessageError?.('Adicione ao menos uma opção de múltipla escolha.');
      return;
    }

    if (tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA) {
      const xOpcaoSemPontos = opcoesMultiplaEscolha.find(xOpcao => xOpcao.points === null);
      if (xOpcaoSemPontos) {
        props.onMessageError?.('Selecione a pontuação de todas as opções.');
        return;
      }
    }
    if (!isQuestaoInterna && tipoResposta === TIPO_RESPOSTA.DICOTOMICA) {
      const xOpcaoSemPontos = opcoesDicotomicas.find(xOpcao => xOpcao.points === null);
      if (xOpcaoSemPontos) {
        props.onMessageError?.('Selecione a pontuação de Sim e Não.');
        return;
      }
    }

    if (
      !isQuestaoInterna &&
      tipoResposta === TIPO_RESPOSTA.NUMERICA &&
      faixasNumericas.length === 0
    ) {
      props.onMessageError?.('Adicione ao menos uma faixa numérica.');
      return;
    }

    if (!isQuestaoInterna && tipoResposta === TIPO_RESPOSTA.NUMERICA) {
      const xFaixaSemLimites = faixasNumericas.find(
        xFaixa => xFaixa.minOp === SEM_LIMITE && xFaixa.maxOp === SEM_LIMITE
      );
      if (xFaixaSemLimites) {
        props.onMessageError?.('Cada faixa numérica precisa ter ao menos um limite definido.');
        return;
      }

      const xFaixaSemPontos = faixasNumericas.find(xFaixa => xFaixa.points === null);
      if (xFaixaSemPontos) {
        props.onMessageError?.('Selecione a pontuação de todas as faixas.');
        return;
      }

      const xIntervalos = [];
      for (let xIndex = 0; xIndex < faixasNumericas.length; xIndex += 1) {
        const xFaixa = faixasNumericas[xIndex];
        const xHasMin = xFaixa.minOp !== SEM_LIMITE;
        const xHasMax = xFaixa.maxOp !== SEM_LIMITE;
        const xMin = xHasMin ? Number(xFaixa.min) : -Infinity;
        const xMax = xHasMax ? Number(xFaixa.max) : Infinity;

        if (
          (xHasMin && (!Number.isFinite(xMin) || xMin < 0)) ||
          (xHasMax && (!Number.isFinite(xMax) || xMax < 0))
        ) {
          props.onMessageError?.('Os limites numéricos devem ser números finitos e não-negativos.');
          return;
        }

        if (xHasMin && xHasMax) {
          const xMinInclusivo = xFaixa.minOp === 'gte';
          const xMaxInclusivo = xFaixa.maxOp === 'lte';
          const xInvalido = xMin > xMax || (xMin === xMax && (!xMinInclusivo || !xMaxInclusivo));
          if (xInvalido) {
            props.onMessageError?.('Há faixa numérica com limites inválidos (mínimo e máximo).');
            return;
          }
        }

        xIntervalos.push({
          idx: xIndex + 1,
          lower: xMin,
          lowerInclusive: xFaixa.minOp === 'gte',
          upper: xMax,
          upperInclusive: xFaixa.maxOp === 'lte'
        });
      }

      const xOrdenados = [...xIntervalos].sort((a, b) => a.lower - b.lower || a.upper - b.upper);
      for (let xIndex = 1; xIndex < xOrdenados.length; xIndex += 1) {
        const xPrev = xOrdenados[xIndex - 1];
        const xCurr = xOrdenados[xIndex];
        const xTemGap =
          xPrev.upper < xCurr.lower ||
          (xPrev.upper === xCurr.lower && !xPrev.upperInclusive && !xCurr.lowerInclusive);
        const xTemOverlap =
          xPrev.upper > xCurr.lower ||
          (xPrev.upper === xCurr.lower && xPrev.upperInclusive && xCurr.lowerInclusive);

        if (xTemOverlap) {
          props.onMessageError?.(
            `As faixas ${xPrev.idx} e ${xCurr.idx} estão sobrepostas. Ajuste os limites.`
          );
          return;
        }

        if (xTemGap) {
          props.onMessageError?.(
            `As faixas ${xPrev.idx} e ${xCurr.idx} possuem gap. Ajuste os limites para cobertura contínua.`
          );
          console.warn('[Avaliacoes] Gap detectado em faixa numérica', xPrev.idx, xCurr.idx);
          return;
        }
      }
    }

    const xDiscriminacoes =
      isQuestaoInterna || tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA
        ? opcoesMultiplaEscolha.map(xOpcao => ({
            pergunta: isQuestaoInterna ? null : xOpcao.pergunta?.trim(),
            discriminacao: xOpcao.discriminacao?.trim(),
            points: Number(xOpcao.points),
            rating: Number(xOpcao.rating || 1)
          }))
        : tipoResposta === TIPO_RESPOSTA.DICOTOMICA
        ? opcoesDicotomicas.map(xOpcao => ({
            pergunta: xOpcao.pergunta,
            discriminacao: xOpcao.discriminacao?.trim(),
            points: Number(xOpcao.points),
            rating: Number(xOpcao.rating || 1)
          }))
        : faixasNumericas.map(xFaixa => ({
            min_op: xFaixa.minOp,
            min: xFaixa.minOp === SEM_LIMITE ? null : Number(xFaixa.min),
            max_op: xFaixa.maxOp,
            max: xFaixa.maxOp === SEM_LIMITE ? null : Number(xFaixa.max),
            discriminacao: xFaixa.discriminacao?.trim(),
            points: Number(xFaixa.points),
            rating: Number(xFaixa.rating || 1)
          }));

    const xPayload = {
      titulo: questao.trim(),
      sessao_id: sessaoId,
      peso,
      modelos_id: modelosSelecionados,
      discriminacao: JSON.stringify(xDiscriminacoes),
      tipo_resposta: isQuestaoInterna ? 'interna' : tipoResposta,
      tipo_exibicao: questaoParaGestor ? 'gestor' : 'padrao'
    };

    try {
      const rRes = await services.mockAvaliacoes.questoesRepo.create(xPayload);

      if (!rRes.ok) {
        props.onMessageError?.(rRes?.error?.message || 'Não foi possível criar a questão.');
        return;
      }

      props.onMessageSuccess?.('Questão criada com sucesso.');
      actions.handleChangeActiveSessaoId(sessaoId);
      navigate('/avaliacoes/settings/questoes');
    } catch (error) {
      props.onMessageError?.('Não foi possível criar a questão.');
    }
  };

  return (
    <BaseTemplate
      title={'Editar Questão'}
      subtitle={'Avaliações'}
      description="Defina o peso de cada questão conforme sua relevância para a avaliação. Os pesos auxiliam na priorização dos critérios e permitem personalizar o impacto das respostas no resultado final."
      maxWidth={'xl'}
      headerRenderActions={
        <Stack direction="row" spacing={2}>
          <Button
            disabled={props.disabledButtons}
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />}
            size="small"
            sx={{ px: 2 }}
            onClick={handleSave}>
            Salvar
          </Button>
        </Stack>
      }>
      <Divider />
      <Stack direction="row" gap={4} py={3}>
        <Stack component="main" flexGrow={1} pt={3} gap={2}>
          <Stack gap={2}>
            <TextField
              label="Título"
              placeholder="Ex.: Classificação de qualidade de gestão [agências de rating]"
              value={questao}
              onChange={pEvent => setQuestao(pEvent.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              multiline
              rows={4}
            />
          </Stack>

          <Divider />

          <Stack gap={2}>
            <Stack gap={2} direction="row">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!questaoParaGestor}
                      onChange={e => setQuestaoParaGestor(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography color="textPrimary" variant="body2">
                      Exibir no questionário para gestor
                    </Typography>
                  }
                />
              </FormGroup>
            </Stack>

            <Divider />

            {questaoParaGestor && (
              <>
                <Stack gap={1}>
                  <Typography
                    color="textPrimary"
                    variant="caption"
                    sx={{ textTransform: 'uppercase' }}>
                    TIPO DE RESPOSTA
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <Button
                      size="small"
                      variant={
                        tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA ? 'contained' : 'outlined'
                      }
                      onClick={() => setTipoResposta(TIPO_RESPOSTA.MULTIPLA_ESCOLHA)}>
                      Múltipla escolha
                    </Button>
                    <Button
                      size="small"
                      variant={tipoResposta === TIPO_RESPOSTA.NUMERICA ? 'contained' : 'outlined'}
                      onClick={() => setTipoResposta(TIPO_RESPOSTA.NUMERICA)}>
                      Numérica
                    </Button>
                    <Button
                      size="small"
                      variant={tipoResposta === TIPO_RESPOSTA.DICOTOMICA ? 'contained' : 'outlined'}
                      onClick={() => setTipoResposta(TIPO_RESPOSTA.DICOTOMICA)}>
                      Dicotômica
                    </Button>
                  </Stack>
                </Stack>

                <Divider />
              </>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography color="textPrimary" variant="h6">
                {questaoParaGestor && tipoResposta === TIPO_RESPOSTA.NUMERICA
                  ? 'Faixas numéricas'
                  : questaoParaGestor && tipoResposta === TIPO_RESPOSTA.DICOTOMICA
                  ? 'Opções dicotômicas'
                  : isQuestaoInterna
                  ? 'Avaliação interna'
                  : 'Avaliação'}
              </Typography>
            </Stack>

            {(isQuestaoInterna || tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA) &&
              opcoesMultiplaEscolha.map((xOpcao, xIndex) => (
                <Stack
                  key={xOpcao.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      <b>{OPCOES_DISCRIMINACAO[xIndex]} Opção</b>
                    </Typography>

                    <IconButton
                      color="error"
                      size="small"
                      disabled={opcoesMultiplaEscolha.length === 1}
                      onClick={() => handleRemoveMultiplaEscolha(xOpcao.id)}>
                      <Icon iconName="delete" color="inherit" size={21} />
                    </IconButton>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <TextField
                      label={isQuestaoInterna ? 'Critério interno' : 'Discriminação'}
                      placeholder="Critério interno"
                      value={xOpcao.discriminacao}
                      onChange={pEvent =>
                        handleUpdateMultiplaEscolha(xOpcao.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                    {questaoParaGestor && (
                      <TextField
                        label="Pergunta"
                        placeholder="Texto exibido para resposta"
                        value={xOpcao.pergunta}
                        onChange={pEvent =>
                          handleUpdateMultiplaEscolha(xOpcao.id, 'pergunta', pEvent.target.value)
                        }
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Stack>
                    <Range
                      value={xOpcao.points}
                      onChange={pValue => handleUpdateMultiplaEscolha(xOpcao.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : opcoesMultiplaEscolha.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {questaoParaGestor &&
              tipoResposta === TIPO_RESPOSTA.DICOTOMICA &&
              opcoesDicotomicas.map((xOpcao, xIndex) => (
                <Stack
                  key={xOpcao.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ minHeight: '31px' }}>
                    <Typography variant="body2" color="textSecondary">
                      <b>Opção {xOpcao.opcao}</b>
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <TextField
                      label="Discriminação"
                      placeholder="Critério interno"
                      value={xOpcao.discriminacao}
                      onChange={pEvent =>
                        handleUpdateDicotomica(xOpcao.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Stack>
                  <Stack>
                    <Range
                      value={xOpcao.points}
                      onChange={pValue => handleUpdateDicotomica(xOpcao.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : opcoesDicotomicas.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {questaoParaGestor &&
              tipoResposta === TIPO_RESPOSTA.NUMERICA &&
              faixasNumericas.map((xFaixa, xIndex) => (
                <Stack
                  key={xFaixa.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      <b>{xFaixa.faixa} Opção</b>
                    </Typography>

                    <IconButton
                      color="error"
                      size="small"
                      disabled={faixasNumericas.length === 1}
                      onClick={() => handleRemoveFaixaNumerica(xFaixa.id)}>
                      <Icon iconName="delete" color="inherit" size={21} />
                    </IconButton>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <Stack gap={1}>
                      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                        <Select
                          label="Operador mín."
                          value={xFaixa.minOp}
                          onChange={pEvent =>
                            handleUpdateFaixaNumerica(xFaixa.id, 'minOp', pEvent.target.value)
                          }
                          size="small"
                          fullWidth
                          variant="outlined">
                          {MIN_OPERADORES.map(xOperador => (
                            <MenuItem key={xOperador.value} value={xOperador.value}>
                              {xOperador.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          label="Mínimo"
                          type="number"
                          size="small"
                          value={xFaixa.min}
                          onChange={pEvent =>
                            handleUpdateFaixaNumerica(xFaixa.id, 'min', pEvent.target.value)
                          }
                          disabled={xFaixa.minOp === SEM_LIMITE}
                          fullWidth
                          variant="outlined"
                        />
                        <Select
                          label="Operador máx."
                          value={xFaixa.maxOp}
                          onChange={pEvent =>
                            handleUpdateFaixaNumerica(xFaixa.id, 'maxOp', pEvent.target.value)
                          }
                          size="small"
                          fullWidth
                          variant="outlined">
                          {MAX_OPERADORES.map(xOperador => (
                            <MenuItem key={xOperador.value} value={xOperador.value}>
                              {xOperador.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          label="Máximo"
                          type="number"
                          size="small"
                          value={xFaixa.max}
                          onChange={pEvent =>
                            handleUpdateFaixaNumerica(xFaixa.id, 'max', pEvent.target.value)
                          }
                          disabled={xFaixa.maxOp === SEM_LIMITE}
                          fullWidth
                          variant="outlined"
                        />
                      </Stack>
                      <Typography color="textSecondary" variant="caption">
                        Sugerido: limite inferior inclusivo ({'>='}) e limite superior exclusivo (
                        {'<'}).
                      </Typography>
                    </Stack>

                    <TextField
                      label="Discriminação"
                      placeholder="Rótulo descritivo da faixa"
                      value={xFaixa.discriminacao}
                      onChange={pEvent =>
                        handleUpdateFaixaNumerica(xFaixa.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Stack>
                  <Stack>
                    <Range
                      value={xFaixa.points}
                      onChange={pValue => handleUpdateFaixaNumerica(xFaixa.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : faixasNumericas.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {tipoResposta !== TIPO_RESPOSTA.DICOTOMICA && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon iconName="insert" size={16} />}
                disabled={
                  !questaoParaGestor || tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA
                    ? opcoesMultiplaEscolha.length >= MAX_DISCRIMINACOES
                    : faixasNumericas.length >= MAX_DISCRIMINACOES
                }
                onClick={
                  !questaoParaGestor || tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA
                    ? handleAddMultiplaEscolha
                    : handleAddFaixaNumerica
                }>
                {!questaoParaGestor || tipoResposta === TIPO_RESPOSTA.MULTIPLA_ESCOLHA
                  ? 'Adicionar opção'
                  : 'Adicionar faixa'}
              </Button>
            )}

            {/* <Divider />
            {questaoParaGestor && <QuestaoTipo />} */}
          </Stack>
        </Stack>

        <Stack
          component="sidebar"
          minWidth={{ lg: 320 }}
          maxWidth={{ lg: 420 }}
          gap={2}
          width="100%"
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 16 },
            alignSelf: { lg: 'flex-start' }
          }}>
          <Card>
            <Stack gap={2} px={2} py={3}>
              <Stack gap={1}>
                <Typography
                  color="textPrimary"
                  variant="caption"
                  sx={{ textTransform: 'uppercase' }}>
                  SESSÃO
                </Typography>
                <Select
                  label="Sessão"
                  value={sessaoId}
                  onChange={pEvent => setSessaoId(pEvent.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ 'aria-label': 'Sessão da questão' }}>
                  {xSessoesQuestoes.map(xSessao => (
                    <MenuItem key={xSessao.sessao_id} value={xSessao.sessao_id}>
                      {xSessao.sessao}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Divider />
              <Stack gap={1}>
                <Typography
                  color="textPrimary"
                  variant="caption"
                  sx={{ textTransform: 'uppercase' }}>
                  MODELOS
                </Typography>
                <Stack flexWrap="wrap" gap={0.5}>
                  <FormGroup>
                    {xModelos.map(xModelo => (
                      <FormControlLabel
                        key={xModelo.id}
                        control={
                          <Checkbox
                            checked={modelosSelecionados.includes(xModelo.id)}
                            onChange={() => handleToggleModelo(xModelo.id)}
                            size="small"
                          />
                        }
                        label={
                          <Typography color="textPrimary" variant="body2">
                            {xModelo.label}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Stack>
              </Stack>
            </Stack>
          </Card>
          <Stack spacing={2}>
            <Button
              disabled={props.disabledButtons}
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="upload" size={16} />}
              size="small"
              sx={{ px: 2 }}
              onClick={handleSave}>
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BaseTemplate>
  );
});

AvaliacoesSettingsQuestoesEdit.displayName = 'AvaliacoesSettingsQuestoesEdit';

export default withMessage(AvaliacoesSettingsQuestoesEdit);
